import { Popover as NextUIPopover } from '@nextui-org/react';
import { ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ShimmerText } from '../../components';
import { inMobile, useWindowDimensions } from '../../hooks/useDimensions';
import SearchInput from '../Input/SearchInput';
import Radio from '../Radio';
import {
  Actions,
  ButtonContent,
  CancelButton,
  Chevron,
  ConfirmButton,
  ContentContainer,
  Description,
  DropdownLabel,
  DropdownWrapper,
  EmptyState,
  IconSelectedIndicator,
  IconTriggerButton,
  ItemButton,
  ItemContainer,
  ItemsContainer,
  ItemTitle,
  MobileContentContainer,
  SearchContainer,
  ShimmerButton,
  StyledModal,
  TriggerButton,
  TriggerButtonContainer,
  TriggerChevronContainer,
  TriggerContent,
  TriggerLabel,
} from './ActionsDropdown.styles';

export interface ActionsDropdownItem {
  label: string;
  value: any;
  description?: ReactNode;
}

export enum ActionsDropdownLabelPosition {
  TOP = 'top',
  LEFT = 'left',
}

export enum ActionsDropdownItemPosition {
  RADIO_LEFT = 'radio-left',
  RADIO_RIGHT = 'radio-right',
}

export enum ActionsDropdownCloseSource {
  CANCEL_BUTTON = 'cancel-button',
  CLOSE_BUTTON = 'close-button',
  BACKDROP = 'backdrop',
}

interface ActionsDropdownProps {
  label?: string;
  items: ActionsDropdownItem[];
  selectedValue?: string;
  setSelectedValue: (value: string) => void;
  secondStepItems?: ActionsDropdownItem[];
  secondStepSelectedValue?: string;
  setSecondStepSelectedValue?: (value: string) => void;
  onSecondStepSelect?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  cancelText?: string;
  confirmText?: string;
  disabled?: boolean;
  onCancel?: () => void;
  onCancelFormButton?: () => void;
  onModalClose?: (source: ActionsDropdownCloseSource) => void;
  onConfirm?: () => void;
  disabledConfirm?: boolean;
  emptyStateText?: string;
  labelPosition?: ActionsDropdownLabelPosition;
  radioPosition?: ActionsDropdownItemPosition;
  showSearch?: boolean;
  iconButton?: ReactNode;
  modalTitle?: string;
  iconSelectedIndicator?: number | string;
  loading?: boolean;
}

const ActionsDropdown = ({
  label,
  loading,
  modalTitle,
  iconSelectedIndicator,
  items,
  selectedValue,
  setSelectedValue,
  secondStepItems,
  secondStepSelectedValue,
  setSecondStepSelectedValue,
  onSecondStepSelect,
  placeholder,
  searchPlaceholder,
  cancelText,
  confirmText,
  emptyStateText,
  disabled,
  onCancel,
  onCancelFormButton,
  onModalClose,
  onConfirm,
  disabledConfirm,
  labelPosition = ActionsDropdownLabelPosition.TOP,
  radioPosition = ActionsDropdownItemPosition.RADIO_LEFT,
  showSearch = true,
  iconButton,
}: ActionsDropdownProps) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const closingViaConfirmRef = useRef(false);
  const suppressCancelFromNextOpenChangeRef = useRef(false);
  const hasSecondStep = Boolean(secondStepItems?.length && setSecondStepSelectedValue);

  const selectedItem = useMemo(
    () => items.find((item) => String(item.value) === selectedValue) ?? null,
    [selectedValue, items],
  );

  const filteredItems = useMemo(
    () => items.filter((item) => item.label.toLowerCase().includes(searchText.toLowerCase().trim())),
    [items, searchText],
  );
  const secondStepFilteredItems = useMemo(
    () => (secondStepItems ?? []).filter((item) => item.label.toLowerCase().includes(searchText.toLowerCase().trim())),
    [secondStepItems, searchText],
  );
  const isSecondStep = hasSecondStep && currentStep === 2;
  const displayedItems = isSecondStep ? secondStepFilteredItems : filteredItems;
  const currentSelectedValue = isSecondStep ? secondStepSelectedValue : selectedValue;

  const handleOpenChange = (open: boolean, closeSource?: ActionsDropdownCloseSource) => {
    setIsOpen(open);
    if (open) {
      closingViaConfirmRef.current = false;
      suppressCancelFromNextOpenChangeRef.current = false;
      setCurrentStep(1);
      return;
    }
    setSearchText('');
    setCurrentStep(1);
    if (suppressCancelFromNextOpenChangeRef.current) {
      suppressCancelFromNextOpenChangeRef.current = false;
      return;
    }
    if (closingViaConfirmRef.current) {
      closingViaConfirmRef.current = false;
      return;
    }

    if (closeSource) {
      onModalClose?.(closeSource);
    }
  };

  const handleCancel = () => {
    suppressCancelFromNextOpenChangeRef.current = true;
    setSearchText('');
    setCurrentStep(1);
    setIsOpen(false);
    onCancelFormButton?.();
    onCancel?.();
  };

  const handleCloseButton = () => {
    handleOpenChange(false, ActionsDropdownCloseSource.CLOSE_BUTTON);
  };

  const handleBackdropDismiss = () => {
    handleOpenChange(false, ActionsDropdownCloseSource.BACKDROP);
  };

  const handleConfirm = () => {
    if (hasSecondStep && currentStep === 1) {
      setSearchText('');
      setCurrentStep(2);
      onConfirm?.();
      return;
    }
    closingViaConfirmRef.current = true;
    setSearchText('');
    setCurrentStep(1);
    setIsOpen(false);
    onConfirm?.();
  };

  const handleItemClick = (value: string) => {
    if (isSecondStep) {
      setSecondStepSelectedValue?.(value);
      onSecondStepSelect?.(value);
      suppressCancelFromNextOpenChangeRef.current = true;
      handleOpenChange(false);
      return;
    }
    setSelectedValue(value);
  };

  const renderRadioButton = (item: ActionsDropdownItem, loading: boolean) => {
    if (loading) {
      return (
        <ItemContainer>
          <ShimmerText $width="1.5rem" $height="1.5rem" $borderRadius="0.5rem" />
        </ItemContainer>
      );
    }
    return (
      <Radio
        $isMobile={inMobile(width)}
        isSelected={currentSelectedValue === String(item.value)}
        value={String(item.value)}
        aria-label={`actionsDropdownRadio_${String(item.value)}`}
      />
    );
  };

  const renderDropdownContent = () => (
    <>
      {showSearch && (
        <SearchContainer>
          <SearchInput
            onTextChange={setSearchText}
            searchTextLocal={searchText}
            placeholder={searchPlaceholder || t('common.search')}
            desktopWidth="100%"
          />
        </SearchContainer>
      )}
      <ItemsContainer $isMobile={inMobile(width)}>
        <Radio.Group value={currentSelectedValue} onChange={(e) => handleItemClick(e.target.value)}>
          {displayedItems.length ? (
            displayedItems.map((item) => (
              <ItemButton
                $isMobile={inMobile(width)}
                key={item.value}
                type="button"
                $isSelected={currentSelectedValue === String(item.value)}
                onClick={() => handleItemClick(String(item.value))}
              >
                {radioPosition === ActionsDropdownItemPosition.RADIO_LEFT ? (
                  <ButtonContent>
                    <ItemContainer>
                      {renderRadioButton(item, loading)}
                      <ItemTitle $isMobile={inMobile(width)} $isSelected={currentSelectedValue === String(item.value)}>
                        {item.label}
                      </ItemTitle>
                    </ItemContainer>
                    <ItemContainer>{item.description && <Description>{item.description}</Description>}</ItemContainer>
                  </ButtonContent>
                ) : (
                  <ButtonContent>
                    {loading ? (
                      <ItemContainer>
                        <ShimmerText $height="1.125rem" $width="18rem" $borderRadius="0.25rem" />
                      </ItemContainer>
                    ) : (
                      <ItemContainer>
                        {item.description && <Description>{item.description}</Description>}
                        <ItemTitle
                          $isMobile={inMobile(width)}
                          $isSelected={currentSelectedValue === String(item.value)}
                        >
                          {item.label}
                        </ItemTitle>
                      </ItemContainer>
                    )}
                    <ItemContainer>{renderRadioButton(item, loading)}</ItemContainer>
                  </ButtonContent>
                )}
              </ItemButton>
            ))
          ) : (
            <EmptyState>{emptyStateText || t('common.noResults')}</EmptyState>
          )}
        </Radio.Group>
      </ItemsContainer>
      {currentStep === 1 && (
        <Actions $isMobile={inMobile(width)}>
          {loading ? (
            <ShimmerButton />
          ) : (
            <CancelButton onPress={handleCancel}>{cancelText || t('common.cancel')}</CancelButton>
          )}
          {loading ? (
            <ShimmerButton />
          ) : (
            <ConfirmButton onPress={handleConfirm} disabled={disabledConfirm}>
              {confirmText || t('common.save')}
            </ConfirmButton>
          )}
        </Actions>
      )}
    </>
  );

  return (
    <DropdownWrapper $labelPosition={labelPosition}>
      {label && <DropdownLabel>{label}</DropdownLabel>}
      {!inMobile(width) ? (
        <NextUIPopover isOpen={isOpen} onOpenChange={handleOpenChange} placement="bottom-left">
          <NextUIPopover.Trigger>
            {iconButton ? (
              <IconTriggerButton disabled={disabled} auto icon={iconButton} />
            ) : (
              <TriggerButton disabled={disabled} $isOpen={isOpen} auto>
                <TriggerContent>
                  <TriggerLabel>{selectedItem?.label ?? placeholder ?? t('common.select')}</TriggerLabel>
                  <TriggerChevronContainer>
                    <Chevron $isOpen={isOpen} />
                  </TriggerChevronContainer>
                </TriggerContent>
              </TriggerButton>
            )}
          </NextUIPopover.Trigger>
          <ContentContainer>{renderDropdownContent()}</ContentContainer>
        </NextUIPopover>
      ) : (
        <>
          {iconButton ? (
            <TriggerButtonContainer>
              <IconTriggerButton disabled={disabled} auto icon={iconButton} onPress={() => handleOpenChange(true)} />
              {iconSelectedIndicator && <IconSelectedIndicator>{String(iconSelectedIndicator)}</IconSelectedIndicator>}
            </TriggerButtonContainer>
          ) : (
            <TriggerButton disabled={disabled} $isOpen={isOpen} auto onPress={() => handleOpenChange(true)}>
              <TriggerContent>
                <TriggerLabel>{selectedItem?.label ?? placeholder ?? t('common.select')}</TriggerLabel>
                <TriggerChevronContainer>
                  <Chevron $isOpen={isOpen} />
                </TriggerChevronContainer>
              </TriggerContent>
            </TriggerButton>
          )}
          <StyledModal
            open={isOpen}
            title={modalTitle ?? label ?? placeholder ?? t('common.select')}
            onClose={handleCloseButton}
            onDismiss={handleBackdropDismiss}
            hideFooter
            noPadding
          >
            <MobileContentContainer>{renderDropdownContent()}</MobileContentContainer>
          </StyledModal>
        </>
      )}
    </DropdownWrapper>
  );
};

export default ActionsDropdown;
